import { useEffect, useRef, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Send, RotateCcw, Loader2 } from "lucide-react";

type TranscriptEntry = {
  direction: "bot" | "user" | "system";
  kind: "text" | "interactive" | "template" | "media" | "system";
  text: string;
  buttons?: Array<{ id: string; text: string }>;
  /** meta opcional do servidor — para templates inclui headerType/headerContent
   *  quando o header for IMAGE/VIDEO/DOCUMENT, e templateName/language; para
   *  `kind: "media"`, o tipo e o nome do arquivo anexado. */
  meta?: {
    templateName?: string;
    language?: string;
    headerType?: "IMAGE" | "VIDEO" | "DOCUMENT" | string;
    headerContent?: string;
    mediaType?: "image" | "video" | "audio" | "document" | string;
    fileName?: string;
  };
  timestamp: number;
};

type Awaiting =
  | { type: "buttons"; buttons: Array<{ id: string; text: string }> }
  | { type: "text" }
  | null;

type Snapshot = {
  sessionId: string;
  status: "running" | "paused" | "completed" | "failed" | "awaiting_ai_reply";
  transcript: TranscriptEntry[];
  awaiting: Awaiting;
};

interface TestChatPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: number | null;
  flowName: string;
  /**
   * Hook chamado **apenas no restart** (clique no ícone de Reiniciar dentro
   * do painel). Usado pelo parent pra auto-salvar mudanças não persistidas
   * — sem isso o engine roda contra o estado do DB stale e o canvas
   * editado fica invisível na simulação. Retornar `false` aborta o restart
   * (ex: validação falhou) e preserva a sessão antiga intacta.
   *
   * **Não é chamado no start inicial** (ao abrir o painel) porque o caller
   * que abre o painel é responsável por garantir o estado salvo antes do
   * `open=true` (ver `handleOpenTestChat` em `WhatsAppFlowBuilder.tsx`).
   * Isso evita save duplicado no caminho normal.
   */
  onBeforeStart?: () => Promise<boolean>;
}

export default function TestChatPanel({ open, onOpenChange, flowId, flowName, onBeforeStart }: TestChatPanelProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Guarda contra disparo duplicado do effect — evita criar várias sessões
  // quando o React re-renderiza com refs novos.
  const startedRef = useRef(false);

  const startMutation = trpc.flows.testStartSession.useMutation();
  const sendMutation = trpc.flows.testSendInput.useMutation();
  const endMutation = trpc.flows.testEndSession.useMutation();

  // Polling apenas enquanto o servidor pode mudar o estado sozinho:
  //   - running: engine rodando (await DB, await IA, etc)
  //   - awaiting_ai_reply: IA processando turno
  // 'paused' não é polled porque a transição só acontece quando o usuário
  // clica num botão (mutation explícita) — polling seria desperdício.
  trpc.flows.testGetSession.useQuery(
    { sessionId: sessionId ?? "" },
    {
      enabled: !!sessionId && open && (snapshot?.status === "running" || snapshot?.status === "awaiting_ai_reply"),
      refetchInterval: 1500,
      onSuccess: (data: unknown) => setSnapshot(data as Snapshot),
    } as any,
  );

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [snapshot?.transcript.length]);

  // Iniciar sessão ao abrir — proteção via ref impede disparos duplicados
  // mesmo se o effect rodar múltiplas vezes.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      return;
    }
    if (startedRef.current || !flowId || sessionId) return;
    startedRef.current = true;
    startMutation.mutate(
      { flowId },
      {
        onSuccess: (data) => {
          setSessionId(data.sessionId);
          setSnapshot(data as Snapshot);
        },
        onError: (err) => {
          toast.error(toUserFriendlyError(err, "Erro ao iniciar teste"));
          startedRef.current = false;
          onOpenChange(false);
        },
      },
    );
    // Apenas open / flowId / sessionId — startMutation e onOpenChange são
    // estáveis o suficiente; deixá-los nas deps causava loop de fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flowId, sessionId]);

  // Encerrar sessão ao fechar
  useEffect(() => {
    if (!open && sessionId) {
      endMutation.mutate({ sessionId });
      setSessionId(null);
      setSnapshot(null);
      setDraft("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRestart = async () => {
    // Auto-salva mudanças do canvas antes de reiniciar, senão o engine
    // re-executa contra o estado do DB stale e edições recentes não aparecem.
    if (onBeforeStart) {
      const ok = await onBeforeStart();
      if (!ok) return;
    }
    if (sessionId) {
      await endMutation.mutateAsync({ sessionId });
      setSessionId(null);
      setSnapshot(null);
      setDraft("");
    }
    if (flowId) {
      startMutation.mutate(
        { flowId },
        {
          onSuccess: (data) => {
            setSessionId(data.sessionId);
            setSnapshot(data as Snapshot);
          },
        },
      );
    }
  };

  const handleSendText = () => {
    if (!sessionId || !draft.trim()) return;
    const text = draft.trim();
    setDraft("");
    sendMutation.mutate(
      { sessionId, type: "text", value: text },
      { onSuccess: (data) => setSnapshot(data as Snapshot), onError: (err) => toast.error(toUserFriendlyError(err)) },
    );
  };

  const handleClickButton = (button: { id: string; text: string }) => {
    if (!sessionId) return;
    // CTA URL: o id começa com "cta_url:<url>". Não dispara mutation — só
    // abre o link em nova aba (mesmo comportamento do WhatsApp real).
    if (button.id.startsWith("cta_url:")) {
      const url = button.id.slice("cta_url:".length);
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    sendMutation.mutate(
      { sessionId, type: "button", value: button.id, buttonText: button.text },
      { onSuccess: (data) => setSnapshot(data as Snapshot), onError: (err) => toast.error(toUserFriendlyError(err)) },
    );
  };

  const isPending = startMutation.isPending || sendMutation.isPending;
  const status = snapshot?.status ?? "running";
  const isFinished = status === "completed" || status === "failed";
  const awaitingButtons = snapshot?.awaiting?.type === "buttons" ? snapshot.awaiting.buttons : null;
  const acceptingText = !awaitingButtons && !isFinished;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col gap-0">
        {/* Header verde estilo WhatsApp */}
        <SheetHeader className="bg-green-600 text-white px-4 py-3 space-y-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-green-700 flex items-center justify-center text-base font-medium">
                🧪
              </div>
              <div>
                <SheetTitle className="text-white text-sm font-medium">{flowName}</SheetTitle>
                <p className="text-xs text-green-100">Chat de teste — modo simulação</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-white hover:bg-green-700 hover:text-white"
              onClick={handleRestart}
              disabled={isPending}
              title="Reiniciar teste"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Banner aviso */}
        <div className="bg-yellow-50 border-b border-yellow-200 px-3 py-2 text-xs text-yellow-900">
          🧪 Modo simulação — nenhuma mensagem está sendo enviada pelo WhatsApp.
        </div>

        {/* Transcrição */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-3 py-4 space-y-2"
          style={{ background: "#ECE5DD" }}
        >
          {!snapshot && (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Iniciando fluxo...
            </div>
          )}
          {snapshot?.transcript.map((entry, idx) => (
            <MessageBubble key={idx} entry={entry} onClickButton={handleClickButton} />
          ))}
          {isFinished && (
            <div className="text-center text-xs text-muted-foreground pt-3">
              {status === "completed" ? "✓ Fluxo finalizado" : "✗ Fluxo falhou"}
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t bg-card px-3 py-3 shrink-0">
          {awaitingButtons ? (
            <div className="text-xs text-muted-foreground text-center py-2">
              Toque em um dos botões acima para continuar.
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendText();
                  }
                }}
                placeholder={
                  isFinished
                    ? "Fluxo finalizado. Reinicie para testar de novo."
                    : status === "awaiting_ai_reply"
                      ? "Digite sua resposta para a IA..."
                      : "Digite uma mensagem..."
                }
                disabled={!acceptingText || isPending}
                className="text-sm"
              />
              <Button
                size="icon"
                onClick={handleSendText}
                disabled={!acceptingText || isPending || !draft.trim()}
                className="bg-green-600 hover:bg-green-700 shrink-0"
                aria-label="Enviar"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MessageBubble({
  entry,
  onClickButton,
}: {
  entry: TranscriptEntry;
  onClickButton: (button: { id: string; text: string }) => void;
}) {
  if (entry.direction === "system") {
    return (
      <div className="flex justify-center">
        <div className="bg-white/80 text-xs text-muted-foreground px-3 py-1 rounded-md max-w-[85%]">
          {entry.text}
        </div>
      </div>
    );
  }
  const isUser = entry.direction === "user";
  // Anexo de um nó de mensagem com arquivo. Sem isto, o passo aparecia como
  // uma bolha em branco na simulação — dava para achar que não tinha enviado.
  const anexo =
    entry.kind === "media"
      ? {
          tipo: String(entry.meta?.mediaType ?? "document"),
          nome: String(entry.meta?.fileName ?? "") || "Arquivo",
        }
      : null;
  const headerMedia =
    entry.meta?.headerType && entry.meta?.headerContent
      ? { type: entry.meta.headerType, url: entry.meta.headerContent }
      : null;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`
          max-w-[80%] rounded-lg p-1.5 shadow-sm
          ${isUser ? "bg-[#DCF8C6]" : "bg-white"}
        `}
      >
        {headerMedia && headerMedia.type === "IMAGE" && (
          <img
            src={headerMedia.url}
            alt="header"
            className="rounded w-full max-h-56 object-cover mb-1.5"
          />
        )}
        {headerMedia && headerMedia.type === "VIDEO" && (
          <video
            src={headerMedia.url}
            controls
            className="rounded w-full max-h-56 mb-1.5"
          />
        )}
        {headerMedia && headerMedia.type === "DOCUMENT" && (
          <a
            href={headerMedia.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-2 py-1.5 mb-1.5 bg-muted/40 rounded text-xs text-blue-600 hover:underline"
          >
            📄 Documento anexado
          </a>
        )}
        {anexo && (
          <div className="flex items-center gap-2 mx-1.5 mt-1 px-2 py-1.5 bg-muted/40 rounded text-xs text-foreground/80">
            <span aria-hidden>{{ image: "🖼", video: "🎬", audio: "🎧" }[anexo.tipo] ?? "📄"}</span>
            <span className="truncate">{anexo.nome}</span>
          </div>
        )}
        {(entry.text || !anexo) && (
          <div className="text-sm text-foreground whitespace-pre-wrap break-words px-1.5 pt-1">{entry.text}</div>
        )}
        {entry.buttons && entry.buttons.length > 0 && (
          <div className="mx-1.5 mt-2 pt-2 border-t border-black/10 flex flex-col gap-1">
            {entry.buttons.map((btn) => {
              const isCta = btn.id.startsWith("cta_url:");
              return (
                <button
                  key={btn.id}
                  type="button"
                  onClick={() => onClickButton(btn)}
                  className="text-sm text-blue-600 hover:bg-blue-50 rounded px-2 py-1.5 text-center transition-colors border border-blue-100 flex items-center justify-center gap-1.5"
                >
                  {isCta && <span aria-hidden>🔗</span>}
                  {btn.text}
                </button>
              );
            })}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground text-right mt-0.5 px-1.5 pb-0.5">
          {new Date(entry.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}
